import { z } from 'zod'

/** Contract limits (customization document v1). */
export const MAX_DOCUMENT_BYTES = 64 * 1024
export const MAX_LINKS = 20
export const MAX_GROUPS = 12
export const MAX_RECORDS_VIEWS = 10
export const MAX_LABEL_LENGTH = 80

export const BUILT_IN_ITEMS = [
  'dashboard',
  'discover',
  'communities',
  'courses',
  'events',
  'documents',
  'invoices',
  'estimates',
  'contracts',
  'appointments',
  'affiliates',
  'loyalty',
] as const

export const WIDGET_IDS = [
  'header',
  'communities-summary',
  'courses-summary',
  'invoices-summary',
  'continue-learning',
  'billing',
  'shared-files',
  'pinned-communities',
  'records',
  'live-events',
] as const

export const FONT_IDS = [
  'inter',
  'lato',
  'montserrat',
  'roboto',
  'poppins',
  'dm-sans',
  'plus-jakarta-sans',
  'space-grotesk',
  'playfair-display',
  'source-serif-4',
  'lora',
  'fraunces',
  'system-sans',
] as const

const COLOR_KEYS = [
  'primary',
  'secondary',
  'accent',
  'emphasis',
  'background',
  'surface',
  'foreground',
  'muted',
  'border',
  'sidebar',
  'success',
  'warning',
  'destructive',
] as const
const TEXT_ON_KEYS = ['primary', 'secondary', 'accent', 'emphasis', 'sidebar'] as const

const ID = /^[A-Za-z0-9_-]{1,64}$/
const id = z.string().regex(ID)
const hex = z.string().regex(/^#[0-9a-fA-F]{6}$/)
const httpsUrl = z.url({ protocol: /^https$/ }).max(2048)
/** Root-relative path: `/x`, never protocol-relative `//host`. */
const rootPath = z
  .string()
  .max(2048)
  .regex(/^\/(?!\/)\S*$/)
const label = z.string().trim().min(1).max(MAX_LABEL_LENGTH)
const localizedText = z.union([label, z.record(z.string().regex(/^[A-Za-z]{2,3}([_-][A-Za-z0-9]{2,8})*$/), label)])
const itemRef = z.union([z.enum(BUILT_IN_ITEMS), z.string().regex(/^(records|link|page):[A-Za-z0-9_-]{1,64}$/)])
const radius = z.enum(['none', 'sm', 'md', 'lg', 'full'])
const border = z.enum(['none', 'subtle', 'strong'])
const depth = z.enum(['flat', 'subtle', 'raised'])

const optionalHexes = <K extends string>(keys: readonly K[]) =>
  Object.fromEntries(keys.map(key => [key, hex.optional()])) as Record<K, z.ZodOptional<typeof hex>>

const colorSeeds = z.strictObject({ ...optionalHexes(COLOR_KEYS), textOn: z.strictObject(optionalHexes(TEXT_ON_KEYS)).optional() })

const maxKeys = (max: number) => (value: Record<string, unknown>) => Object.keys(value).length <= max

const theme = z.strictObject({
  preset: z.string().max(64).optional(),
  mode: z.enum(['light', 'dark', 'system']).optional(),
  colors: z.strictObject({ light: colorSeeds.optional(), dark: colorSeeds.optional() }).optional(),
  fonts: z.strictObject({ heading: z.enum(FONT_IDS).optional(), body: z.enum(FONT_IDS).optional() }).optional(),
  textSize: z.enum(['small', 'default', 'large']).optional(),
  labels: z.enum(['normal', 'uppercase']).optional(),
  density: z.enum(['compact', 'comfortable', 'roomy']).optional(),
  shapes: z
    .strictObject({
      box: z.strictObject({ radius: radius.optional(), border: border.optional(), depth: depth.optional() }).optional(),
      button: z.strictObject({ radius: radius.optional(), border: border.optional(), depth: depth.optional() }).optional(),
      field: z.strictObject({ radius: radius.optional(), border: border.optional() }).optional(),
      selector: z.strictObject({ radius: radius.optional() }).optional(),
    })
    .optional(),
})

const branding = z.strictObject({
  name: label.optional(),
  description: z.string().max(500).optional(),
  logo: httpsUrl.optional(),
  logoDark: httpsUrl.optional(),
  favicon: httpsUrl.optional(),
  loginImage: httpsUrl.optional(),
})

const navLink = z.strictObject({
  label: localizedText,
  icon: z.string().regex(/^[a-z0-9-]{1,40}$/),
  url: z.union([httpsUrl, rootPath]),
  open: z.enum(['in-portal', 'new-tab', 'same-tab']),
  path: rootPath.optional(),
  audience: z.enum(['members', 'everyone']).optional(),
})

const navigation = z.strictObject({
  landing: itemRef.optional(),
  order: z.array(itemRef).max(100).optional(),
  groupOrder: z
    .array(id)
    .max(MAX_GROUPS + 8)
    .optional(),
  items: z
    .record(itemRef, z.strictObject({ label: localizedText.optional(), group: id.nullable().optional(), hidden: z.boolean().optional() }))
    .optional(),
  groups: z
    .record(id, z.strictObject({ label: localizedText }))
    .refine(maxKeys(MAX_GROUPS))
    .optional(),
  links: z.record(id, navLink).refine(maxKeys(MAX_LINKS)).optional(),
})

const dashboard = z.strictObject({
  order: z.array(z.enum(WIDGET_IDS)).max(WIDGET_IDS.length).optional(),
  widgets: z
    .partialRecord(
      z.enum(WIDGET_IDS),
      z.strictObject({
        enabled: z.boolean().optional(),
        width: z.enum(['third', 'two-thirds', 'full']).optional(),
        style: z.enum(['card', 'plain']).optional(),
        options: z.record(z.string(), z.unknown()).optional(),
      })
    )
    .optional(),
})

/** A Records page's address, `/records/<slug>`: the rule the records layer and revex apply. */
const recordsSlug = z.string().max(40).regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/)
const records = z.strictObject({
  order: z.array(recordsSlug).max(MAX_RECORDS_VIEWS).optional(),
  views: z.record(recordsSlug, z.looseObject({})).refine(maxKeys(MAX_RECORDS_VIEWS)).optional(),
  payments: z.enum(['auto', 'test', 'live']).optional(),
})

const byteLength = (value: unknown) => new TextEncoder().encode(JSON.stringify(value)).length

/** Strict schema for writers: unknown keys and out-of-limit values are rejected. */
export const portalCustomizationSchema = z
  .strictObject({
    version: z.literal(1),
    theme: theme.optional(),
    branding: branding.optional(),
    navigation: navigation.optional(),
    dashboard: dashboard.optional(),
    records: records.optional(),
  })
  .refine(doc => byteLength(doc) <= MAX_DOCUMENT_BYTES, { message: `document exceeds ${MAX_DOCUMENT_BYTES} bytes` })

export type PortalCustomization = z.infer<typeof portalCustomizationSchema>
export type PortalTheme = NonNullable<PortalCustomization['theme']>
export type PortalNavigation = NonNullable<PortalCustomization['navigation']>
export type NavLink = z.infer<typeof navLink>
export type LocalizedText = z.infer<typeof localizedText>
export type BuiltInItem = (typeof BUILT_IN_ITEMS)[number]
export type WidgetId = (typeof WIDGET_IDS)[number]
export type FontId = (typeof FONT_IDS)[number]

const EMPTY: PortalCustomization = { version: 1 }

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value)

const unwrap = (schema: z.ZodType): z.ZodType =>
  schema instanceof z.ZodOptional || schema instanceof z.ZodNullable ? unwrap(schema.unwrap() as z.ZodType) : schema

/**
 * Keeps every part of `value` that is valid against `schema`, dropping the rest:
 * unknown keys, invalid fields, invalid record entries and invalid array items.
 * A container that still fails its own checks afterwards (e.g. a link missing its
 * url, a map over its size limit) is dropped as a whole. Returns undefined when
 * nothing usable is left.
 */
function keepValid(schema: z.ZodType, value: unknown): unknown {
  const parsed = schema.safeParse(value)
  if (parsed.success) return parsed.data

  const inner = unwrap(schema)
  let candidate: unknown
  if (inner instanceof z.ZodObject && isPlainObject(value)) {
    const entries = Object.entries(inner.shape as Record<string, z.ZodType>).map(([key, child]) => [key, keepValid(child, value[key])])
    candidate = Object.fromEntries(entries.filter(([, kept]) => kept !== undefined))
  } else if (inner instanceof z.ZodRecord && isPlainObject(value)) {
    const keyType = inner.keyType as z.ZodType
    const entries = Object.entries(value)
      .filter(([key]) => keyType.safeParse(key).success)
      .map(([key, entry]) => [key, keepValid(inner.valueType as z.ZodType, entry)])
    candidate = Object.fromEntries(entries.filter(([, kept]) => kept !== undefined))
  } else if (inner instanceof z.ZodArray && Array.isArray(value)) {
    candidate = value.map(item => keepValid(inner.element as z.ZodType, item)).filter(item => item !== undefined)
  } else {
    return undefined
  }

  const reparsed = schema.safeParse(candidate)
  return reparsed.success ? reparsed.data : undefined
}

/**
 * Forgiving reader: never throws. A missing document, an unknown version or an
 * oversized/unserialisable one resolves to an empty v1 document; otherwise each
 * invalid value is dropped and the valid rest is kept.
 */
export function resolveCustomization(input: unknown): PortalCustomization {
  if (!isPlainObject(input) || input.version !== 1) return EMPTY
  try {
    if (byteLength(input) > MAX_DOCUMENT_BYTES) return EMPTY
    return (keepValid(portalCustomizationSchema, input) as PortalCustomization | undefined) ?? EMPTY
  } catch {
    // Cyclic or non-JSON input (e.g. BigInt) cannot be a stored document.
    return EMPTY
  }
}
